import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { TilopayFetchAPI } from "../../api/Tilopay";
import {
  TILOPAY_SDK_URL,
  buildInitParams,
  canUseSavedCards,
  getFrameUrl,
  getReturnUrl,
  getStatusCopy,
  isValidEmail,
  validateInitResult,
  writePending,
} from "./tilopayHelpers";
import "./TilopayPaymentModal.css";

const SLOW_PAYMENT_MS = 25000;
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_MS = 5 * 60 * 1000;
const FRAME_READY_TIMEOUT_MS = 15000;
const INIT_TIMEOUT_MS = 25000;
const DEFAULT_FRAME_HEIGHT = 200;
const SDK_ERROR_MESSAGE = "No pudimos cargar la pasarela de pago. Revisa tu conexión o desactiva bloqueadores de contenido.";

const Icon = ({ name, size = 18 }) => {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round", "aria-hidden": "true", focusable: "false" };
  switch (name) {
    case "lock":
      return (<svg {...common}><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>);
    case "shield":
      return (<svg {...common}><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6l7-3z" /><path d="M9 12l2 2 4-4" /></svg>);
    case "alert":
      return (<svg {...common}><circle cx="12" cy="12" r="9" /><path d="M12 8v5" /><path d="M12 16.5h.01" /></svg>);
    default:
      return null;
  }
};

/**
 * Pago con tarjeta mediante el SDK JavaScript V2 de Tilopay, dentro del checkout.
 *
 * El formulario del SDK vive en un iframe del mismo origen (public/tilopay-frame.html): asi el redirect final del SDK
 * ocurre dentro del iframe y el cliente nunca sale del checkout. El modal habla con el iframe por postMessage
 * (ver public/tilopay-frame.js) y confirma el resultado preguntando al servidor (GET /tilopay/status), que lo verifica
 * contra Tilopay: ni el mensaje del iframe ni la URL de retorno deciden si el pago fue aprobado.
 *
 * El modal tampoco decide ni el monto ni la orden: `prepareSession` (Checkout) pide al servidor el intento de pago y
 * `createPreOrder` crea la orden temporal vinculada a ese intento justo antes de cobrar.
 *
 * Props:
 *  - show, onClose
 *  - payer: { name, email, phone, address, accountEmail }  (accountEmail: correo de la cuenta; habilita tarjetas guardadas)
 *  - prepareSession(): Promise<session>      (session = respuesta de POST /tilopay/session)
 *  - createPreOrder(session): Promise<void>  (idempotente por session.order_number)
 *  - onApproved(view): el servidor confirmo el pago y registro el pedido (view.order_code)
 */
const TilopayPaymentModal = ({ show, onClose, payer = {}, prepareSession, createPreOrder, onApproved }) => {
  const [phase, setPhase] = useState("boot"); // boot | need_email | preparing | initializing | ready | paying | verifying | error | fatal
  const [message, setMessage] = useState("");
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState("");
  const [slow, setSlow] = useState(false);
  const [frameKey, setFrameKey] = useState(0);
  const [frameHeight, setFrameHeight] = useState(DEFAULT_FRAME_HEIGHT);

  const frameRef = useRef(null);
  const sessionRef = useRef(null);
  const emailRef = useRef("");
  const payerRef = useRef(payer);
  const phaseRef = useRef("boot");
  const submittingRef = useRef(false);
  const preOrderForRef = useRef(null);
  const mountedRef = useRef(true);
  const bootedRef = useRef(false);
  const frameReadyRef = useRef(false);
  const pendingInitRef = useRef(null);
  const frameLoadsRef = useRef(0);
  const pollingRef = useRef(false);
  const initTimerRef = useRef(null);
  const handlersRef = useRef({});

  payerRef.current = payer;
  phaseRef.current = phase;

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const safeSet = useCallback((fn) => { if (mountedRef.current) fn(); }, []);

  const sendToFrame = (data) => {
    const target = frameRef.current && frameRef.current.contentWindow;
    if (target) target.postMessage(data, window.location.origin);
  };

  const clearInitTimer = () => { window.clearTimeout(initTimerRef.current); initTimerRef.current = null; };

  const fail = (text, fatal = false) => {
    clearInitTimer();
    submittingRef.current = false;
    safeSet(() => { setMessage(text); setPhase(fatal ? "fatal" : "error"); });
  };

  const flushInit = () => {
    const pending = pendingInitRef.current;
    if (!pending || !frameReadyRef.current) return;
    pendingInitRef.current = null;
    const payerData = { ...payerRef.current, email: emailRef.current };
    sendToFrame({
      type: "tilopay:init",
      sdkUrl: TILOPAY_SDK_URL,
      params: buildInitParams({ session: pending, payer: payerData, returnUrl: getReturnUrl() }),
      cardsEnabled: canUseSavedCards(pending, payerData),
    });
  };

  // Pide al iframe que inicie el SDK (ahora, o cuando avise que esta listo).
  const startInit = (currentSession) => {
    safeSet(() => { setPhase("initializing"); setMessage(""); });
    pendingInitRef.current = currentSession;
    clearInitTimer();
    initTimerRef.current = window.setTimeout(() => {
      if (phaseRef.current === "initializing") fail(SDK_ERROR_MESSAGE);
    }, INIT_TIMEOUT_MS);
    flushInit();
  };

  const preparePayment = async () => {
    safeSet(() => { setPhase("preparing"); setMessage(""); });
    try {
      const created = await prepareSession();
      if (!created?.order_number || !created?.token) throw new Error("Respuesta invalida del servidor");
      sessionRef.current = created;
      safeSet(() => setSession(created));
      startInit(created);
    } catch (err) {
      const apiMessage = err?.response?.data?.error || err?.response?.data?.message;
      // Sin Tilopay habilitado o pedido ya pagado no tiene caso reintentar.
      fail(apiMessage || err?.message || "No pudimos iniciar el pago. Intenta de nuevo.",
        ["TILOPAY_NOT_ENABLED", "TILOPAY_ALREADY_PAID"].includes(err?.response?.data?.code));
    }
  };

  const bootAfterFrameReady = () => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    if (!isValidEmail(emailRef.current)) {
      safeSet(() => setPhase("need_email"));
      return;
    }
    preparePayment();
  };

  // Estado real del pago, verificado por el servidor. Devuelve true si ya es un resultado definitivo.
  // Tilopay puede tener ya un registro no aprobado de la orden mientras el cliente todavia resuelve el desafio 3D Secure
  // (el formulario sigue abierto en el iframe). Por eso un "rechazado" solo cuenta cuando el SDK ya termino (el iframe
  // redirigio o avisó el resultado): `allowDecline` es false en las consultas periodicas con el desafio aun abierto.
  const pollStatus = async (allowDecline = true) => {
    const current = sessionRef.current;
    if (!current || pollingRef.current) return false;
    pollingRef.current = true;
    try {
      const response = await TilopayFetchAPI.getStatus(current.order_number);
      const view = response?.data?.data;
      if (!view || !mountedRef.current) return false;

      if (view.status === "registered") {
        writePending(null);
        if (onApproved) onApproved(view);
        else window.location.assign(`/tilopay/result?order=${encodeURIComponent(current.order_number)}`);
        return true;
      }
      if (view.status === "declined") {
        if (!allowDecline) return false; // el 3DS puede seguir en curso: se vuelve a consultar
        writePending(null);
        fail(getStatusCopy("declined").text);
        return true;
      }
      if (["abandoned", "mismatch", "duplicate_approved", "refunding", "refunded", "refund_failed"].includes(view.status)) {
        writePending(null);
        const copy = getStatusCopy(view.status);
        fail(`${copy.title}. ${copy.text}`, true);
        return true;
      }
      return false; // created / approved_unlinked / registering: sigue pendiente
    } catch (err) {
      return false; // sin respuesta: se reintenta en el siguiente ciclo
    } finally {
      pollingRef.current = false;
    }
  };

  // Mensajes del iframe (ver public/tilopay-frame.js). Se registra una vez; lee todo por refs.
  handlersRef.current.onFrameMessage = (event) => {
    const frameWindow = frameRef.current && frameRef.current.contentWindow;
    if (!frameWindow || event.source !== frameWindow) return;
    const data = event.data || {};
    const sameOrigin = event.origin === window.location.origin;

    // El resultado llega desde la URL de retorno del backend (puede ser otro origen). Es solo un aviso: se verifica con el servidor.
    if (data.type === "tilopay:result") {
      pollStatus();
      return;
    }
    if (!sameOrigin) return;

    if (data.type === "tilopay:ready") {
      frameReadyRef.current = true;
      flushInit();
      bootAfterFrameReady();
    } else if (data.type === "tilopay:height") {
      const height = Number(data.height);
      if (Number.isFinite(height)) setFrameHeight(Math.max(150, Math.min(900, height)));
    } else if (data.type === "tilopay:init-result") {
      clearInitTimer();
      if (!data.ok) {
        fail(data.code === "SDK_LOAD" ? SDK_ERROR_MESSAGE : "No pudimos preparar el pago. Intenta de nuevo.");
        return;
      }
      const problem = validateInitResult(data.init, sessionRef.current);
      if (problem) {
        // Un entorno que no coincide no se reintenta: cobraria en el modo equivocado.
        fail(problem, /entorno/i.test(problem));
        return;
      }
      safeSet(() => setPhase("ready"));
    } else if (data.type === "tilopay:pay-result" && phaseRef.current === "paying" && data.message) {
      writePending(null);
      fail(data.message);
    }
  };

  useEffect(() => {
    if (!show) return undefined;
    const listener = (event) => handlersRef.current.onFrameMessage(event);
    window.addEventListener("message", listener);
    return () => window.removeEventListener("message", listener);
  }, [show]);

  // Reinicio cada vez que se abre el modal.
  useEffect(() => {
    if (!show) {
      bootedRef.current = false;
      frameReadyRef.current = false;
      pendingInitRef.current = null;
      sessionRef.current = null;
      submittingRef.current = false;
      preOrderForRef.current = null;
      frameLoadsRef.current = 0;
      emailRef.current = "";
      clearInitTimer();
      setSession(null);
      setPhase("boot");
      setMessage("");
      setSlow(false);
      setFrameHeight(DEFAULT_FRAME_HEIGHT);
      return;
    }
    emailRef.current = String(payer.email || "").trim();
    setEmail(emailRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Si el iframe no avisa que esta listo, es que no cargo (bloqueo, hosting sin el archivo, red).
  useEffect(() => {
    if (!show) return undefined;
    const timer = window.setTimeout(() => {
      if (!frameReadyRef.current) fail(SDK_ERROR_MESSAGE);
    }, FRAME_READY_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, frameKey]);

  // Mientras se cobra o se verifica se pregunta al servidor cada pocos segundos: asi el pedido se completa aunque el
  // aviso del iframe no llegue. Tras 5 minutos se deja de insistir y se ofrece verificar a mano.
  useEffect(() => {
    if (!["paying", "verifying"].includes(phase)) return undefined;
    const startedAt = Date.now();
    const timer = window.setInterval(async () => {
      if (Date.now() - startedAt > MAX_POLL_MS) { window.clearInterval(timer); setSlow(true); return; }
      if (await pollStatus(phaseRef.current === "verifying")) window.clearInterval(timer);
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Aviso si el pago tarda en completarse (3DS lento, red caida).
  useEffect(() => {
    if (!["paying", "verifying"].includes(phase)) { setSlow(false); return undefined; }
    const timer = setTimeout(() => setSlow(true), SLOW_PAYMENT_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  // El iframe vuelve a cargar cuando el SDK redirige a la URL de retorno: ya no hay formulario, solo se verifica el pago.
  const handleFrameLoad = () => {
    frameLoadsRef.current += 1;
    if (frameLoadsRef.current > 1 && phaseRef.current === "paying") safeSet(() => setPhase("verifying"));
  };

  const handleEmailSubmit = (event) => {
    event.preventDefault();
    if (!isValidEmail(email)) { setMessage("Ingresa un correo valido para continuar."); return; }
    emailRef.current = email.trim();
    setMessage("");
    preparePayment();
  };

  const goToResult = (orderNumber) => {
    window.location.assign(`/tilopay/result?order=${encodeURIComponent(orderNumber)}`);
  };

  const handlePay = async () => {
    // Doble clic: la segunda llamada se descarta antes de tocar el SDK.
    if (submittingRef.current || phaseRef.current !== "ready") return;
    const current = sessionRef.current;
    if (!current) return;
    submittingRef.current = true;
    setPhase("paying");
    setMessage("");

    try {
      if (preOrderForRef.current !== current.order_number) {
        await createPreOrder(current);
        preOrderForRef.current = current.order_number;
      }
      // Si el usuario cierra o recarga durante el pago, la pagina de resultado puede retomar este intento.
      writePending({ order_number: current.order_number, at: Date.now() });
      sendToFrame({ type: "tilopay:pay" });
      // El resultado llega por el polling al servidor (o por el aviso del iframe); un error del SDK, por 'pay-result'.
    } catch (err) {
      writePending(null);
      const apiMessage = err?.response?.data?.error || err?.response?.data?.message;
      fail(apiMessage || "No pudimos procesar el pago. Intenta de nuevo.");
    }
  };

  // Recarga el iframe (formulario limpio, SDK nuevo) y vuelve a iniciar el SDK con la sesion indicada.
  const reloadFrame = () => {
    frameReadyRef.current = false;
    frameLoadsRef.current = 0;
    setFrameKey((key) => key + 1);
  };

  // Tras un error: si Tilopay no tiene transaccion para esta orden se reutiliza; si no, se crea un intento nuevo.
  const handleRetry = async () => {
    const current = sessionRef.current;
    safeSet(() => { setPhase("preparing"); setMessage(""); });
    if (current) {
      try {
        const response = await TilopayFetchAPI.getStatus(current.order_number);
        const status = response?.data?.data?.status;
        if (status === "created") { reloadFrame(); startInit(current); return; }
        if (["registered", "approved_unlinked", "registering"].includes(status)) { goToResult(current.order_number); return; }
      } catch (err) { /* sin respuesta: se crea un intento nuevo */ }
    }
    sessionRef.current = null;
    preOrderForRef.current = null;
    reloadFrame();
    await preparePayment();
  };

  const working = ["paying", "verifying"].includes(phase);
  const busy = working && !slow;
  const handleHide = () => { if (!busy) onClose(); };
  const showFrame = ["initializing", "ready", "paying", "error"].includes(phase) && !!session;
  const isWorking = phase === "boot" || phase === "preparing" || phase === "initializing";

  return (
    <Modal
      show={show}
      onHide={handleHide}
      centered
      scrollable
      backdrop="static"
      keyboard={!busy}
      dialogClassName="tlpy-modal"
      contentClassName="tlpy-content"
    >
      <Modal.Header closeButton={!busy} className="tlpy-header">
        <span className="tlpy-badge"><Icon name="lock" size={22} /></span>
        <div className="tlpy-heading">
          <Modal.Title as="h2" className="tlpy-title">Pagar con tarjeta</Modal.Title>
          <p className="tlpy-subtitle">Pago seguro procesado por Tilopay</p>
        </div>
      </Modal.Header>

      <Modal.Body className="tlpy-body">
        {session && (
          <div className="tlpy-total">
            <span className="tlpy-total-label">Total a pagar</span>
            <span className="tlpy-total-amount">${session.amount}</span>
          </div>
        )}

        {isWorking && (
          <div className="tlpy-state" role="status">
            <span className="tlpy-spinner" aria-hidden="true" />
            <span>{phase === "boot" ? "Cargando pasarela de pago..." : "Preparando tu pago seguro..."}</span>
          </div>
        )}

        {phase === "verifying" && (
          <div className="tlpy-state" role="status">
            <span className="tlpy-spinner" aria-hidden="true" />
            <span>Confirmando tu pago con el banco...</span>
          </div>
        )}

        {phase === "need_email" && (
          <form onSubmit={handleEmailSubmit} className="tlpy-form">
            <div className="tlpy-field">
              <label htmlFor="tlpy_email" className="tlpy-label">Correo electrónico (requerido por el banco)</label>
              <input id="tlpy_email" type="email" className="tlpy-input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" placeholder="tucorreo@ejemplo.com" />
            </div>
            <button type="submit" className="tlpy-btn tlpy-btn--primary" style={{ width: "100%" }}>Continuar</button>
          </form>
        )}

        {message && (
          <div className={`tlpy-alert ${phase === "fatal" ? "tlpy-alert--error" : "tlpy-alert--warn"}`} role="alert">
            <Icon name="alert" />
            <span>{message}</span>
          </div>
        )}

        {/* Formulario del SDK: vive en un iframe del mismo origen (el redirect final ocurre dentro y no saca al cliente del checkout).
            Sin allow-top-navigation: nada de lo que cargue puede cambiar la pagina del checkout. Se oculta, no se desmonta,
            para conservar el SDK iniciado. */}
        {show && (
          <iframe
            key={frameKey}
            ref={frameRef}
            title="Formulario de pago seguro"
            src={getFrameUrl()}
            className="tlpy-frame"
            style={{ height: frameHeight, display: showFrame ? "block" : "none" }}
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
            onLoad={handleFrameLoad}
          />
        )}

        {showFrame && phase !== "paying" && (
          <div className="tlpy-secure">
            <Icon name="shield" size={16} />
            <span>Tus datos de tarjeta viajan cifrados directo a Tilopay; SmartPay no los almacena.</span>
          </div>
        )}

        {phase === "paying" && (
          <p className="tlpy-wait">Procesando tu pago, no cierres esta ventana...</p>
        )}

        {working && slow && session && (
          <p className="tlpy-wait">
            Todavía no recibimos la confirmación. <button type="button" className="tlpy-link" onClick={() => goToResult(session.order_number)}>Verificar estado del pago</button>
          </p>
        )}
      </Modal.Body>

      <Modal.Footer className="tlpy-footer">
        <button type="button" className="tlpy-btn tlpy-btn--ghost" onClick={handleHide} disabled={busy}>Cancelar</button>
        {phase === "error" && <button type="button" className="tlpy-btn tlpy-btn--primary" onClick={handleRetry}>Intentar de nuevo</button>}
        {phase === "fatal" && <button type="button" className="tlpy-btn tlpy-btn--primary" onClick={onClose}>Cerrar</button>}
        {["ready", "paying", "verifying"].includes(phase) && (
          <button type="button" className="tlpy-btn tlpy-btn--primary" onClick={handlePay} disabled={phase !== "ready"}>
            {working
              ? (<><span className="tlpy-spinner" aria-hidden="true" />Procesando...</>)
              : (<><Icon name="lock" size={16} />{`Pagar${session ? ` $${session.amount}` : ""}`}</>)}
          </button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default TilopayPaymentModal;
